import { ArrowRight, ChevronDown } from "lucide-react";
import { bookHref } from "@/utils/booking";
import { Link } from "react-router-dom";
import { trackEvent } from "@/utils/tracking";


// Hero image served from Unsplash's on-the-fly CDN. We ask for WebP (50% smaller
// than JPG) and a responsive srcset so mobile devices download ~49KB instead of
// 428KB. The <link rel="preload"> in index.html kickstarts the download before
// React even boots.
const HERO_BASE = "https://images.unsplash.com/photo-1773867567872-3ad1fa481082?crop=entropy&cs=srgb&fm=webp";
const HERO_SRC = `${HERO_BASE}&w=1920&q=80`;
const HERO_SRCSET = [
  `${HERO_BASE}&w=640&q=70 640w`,
  `${HERO_BASE}&w=1024&q=75 1024w`,
  `${HERO_BASE}&w=1536&q=78 1536w`,
  `${HERO_BASE}&w=1920&q=80 1920w`,
].join(", ");

export default function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[100vh] flex items-center" aria-label="Hero — I.T. Help of SWFL">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_SRC}
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Premium home theater installation by I.T. Help of SWFL in Naples, Florida"
          className="w-full h-full object-cover"
          width="1920"
          height="1080"
          fetchPriority="high"
          decoding="async"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-24 pb-10 md:pb-16">
        <div className="max-w-2xl">
          <p
            className="text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 md:mb-6 animate-fade-in-up"
            style={{ animationDelay: '0.1s', opacity: 0, animationFillMode: 'forwards' }}
          >
            Serving Naples, Port Royal, Marco Island & SWFL
          </p>

          <h1
            className="text-[2.5rem] md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[1.05] mb-4 md:mb-6 animate-fade-in-up"
            style={{ fontFamily: 'Outfit, sans-serif', animationDelay: '0.2s', opacity: 0, animationFillMode: 'forwards' }}
          >
            TV Mounting, Smart Home & IT Services
            <br />
            <span className="text-blue-400">in Naples & Southwest Florida</span>
          </h1>

          <p
            className="text-xs md:text-sm text-slate-500 mb-2 -mt-2 md:-mt-4 animate-fade-in-up"
            style={{ animationDelay: '0.25s', opacity: 0, animationFillMode: 'forwards' }}
          >
            I.T. Help of SWFL — Premium Technology for Luxury Living
          </p>

          <p
            className="text-base md:text-xl text-slate-400 leading-relaxed mb-6 md:mb-10 max-w-xl animate-fade-in-up"
            style={{ animationDelay: '0.35s', opacity: 0, animationFillMode: 'forwards' }}
          >
            Professional TV mounting, smart home installation, home theater design, WiFi networking,
            and IT support — crafted for Southwest Florida&apos;s most discerning homeowners and businesses.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fade-in-up"
            style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}
          >
            <a
              href={bookHref({ campaign: "home", content: "hero-book-now" })}
              data-testid="hero-book-now-btn"
              onClick={() => trackEvent("book_now_click", { location: "hero", campaign: "home" })}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 font-medium min-h-[48px] px-6 md:px-8 py-3.5 md:py-4 rounded-lg transition-all btn-glow text-base"
            >
              Book a Consultation
              <ArrowRight size={18} />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-btn"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/20 hover:border-white/40 hover:bg-white/5 font-medium min-h-[48px] px-6 md:px-8 py-3.5 md:py-4 rounded-lg transition-all text-base"
            >
              Explore Services
            </a>
          </div>

          {/* Smart-intent quick-pick — catches homepage visitors from long-tail queries.
              Each pill drops the user straight onto the matching service page. */}
          <div
            className="mt-6 md:mt-10 flex flex-wrap gap-2 max-w-2xl animate-fade-in-up"
            style={{ animationDelay: '0.55s', opacity: 0, animationFillMode: 'forwards' }}
            data-testid="hero-quickpick"
          >
            <p className="w-full text-[11px] md:text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-1">
              What Are You Looking For?
            </p>
            {[
              { label: "TV Mounting", to: "/services/tv-mounting" },
              { label: "Smart Home", to: "/services/smart-home" },
              { label: "Home Theater", to: "/services/home-theater" },
              { label: "WiFi & Networking", to: "/services/wifi-network" },
              { label: "Security Cameras", to: "/services/security-cameras" },
              { label: "IT Support", to: "/services/it-support" },
              { label: "Business IT", to: "/services/business-it" },
              { label: "Home Automation", to: "/services/smart-home" },
            ].map((pill) => (
              <Link
                key={pill.label}
                to={pill.to}
                data-testid={`hero-quickpick-${pill.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="inline-flex items-center min-h-[44px] px-4 py-2.5 rounded-full text-sm border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:bg-blue-500/10 hover:border-blue-400/40 transition-all"
              >
                {pill.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <a
        href="#services"
        aria-label="Scroll to services"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 inline-flex items-center justify-center min-w-[44px] min-h-[44px] animate-bounce text-white/40 hover:text-white/70 transition-colors rounded-full"
      >
        <ChevronDown size={28} />
      </a>
    </section>
  );
}
